// Hook que devuelve el `style` para el <main> de cada pantalla pública
// con el fondo configurable (global.bg_landscape_url / bg_portrait_url).
//
// Por qué hook y no inline:
//   1) Se aplica en Landing + Ruleta + Raspadita + Memoria — 4 lugares
//      con la misma lógica. Antes solo Landing lo hacía y se decidió
//      extender al resto.
//   2) Necesita reaccionar a rotación de pantalla / resize del kiosko.
//      Antes Landing leía window.innerWidth solo en el primer render →
//      si el operador rotaba la TV, el fondo se quedaba con la versión
//      vieja hasta refrescar. Acá lo arreglo con un listener.
//
// Detección de orientación: window.innerWidth > innerHeight. Es más
// confiable en kiosko que `screen.orientation` que a veces reporta
// estado raro en Chromium-on-Android-on-TV.

import { useEffect, useState } from "react";

export type BackgroundGlobalLike = {
  bg_landscape_url: string | null;
  bg_portrait_url: string | null;
};

// Helper PURO (sin hooks): construye el style de fondo con el MISMO overlay
// oscuro (rgba 0.4) y la misma elección portrait/landscape que la pantalla
// real. Lo usan el hook (pantalla del cliente) y el preview del admin, así
// el fondo del preview coincide 100% con el del kiosko (incluido el overlay,
// que antes el preview no aplicaba → se veía más claro que la realidad).
export function buildBackgroundStyle(
  global: BackgroundGlobalLike | undefined | null,
  isLandscape: boolean,
): React.CSSProperties | undefined {
  if (!global) return undefined;
  const url = isLandscape ? global.bg_landscape_url : global.bg_portrait_url;
  if (!url) return undefined;
  return {
    backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${url})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };
}

export function useBackgroundStyle(
  global: BackgroundGlobalLike | undefined | null,
): React.CSSProperties | undefined {
  const [isLandscape, setIsLandscape] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > window.innerHeight;
  });

  useEffect(() => {
    function onResize() {
      setIsLandscape(window.innerWidth > window.innerHeight);
    }
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Overlay oscuro (rgba 0.4) + imagen, delegado al helper puro para que la
  // pantalla real y el preview del admin usen exactamente lo mismo.
  return buildBackgroundStyle(global, isLandscape);
}
